/**
 * Diagnostic — moteur de recommandation.
 *
 * Transcription de l'arbre v2 (kqueo-diagnostic-v2-arbre-tranche_MSH.xlsx,
 * onglets « Questions v2 » à « Catalogue v2 »). Aucun accès au DOM : c'est du
 * calcul pur, appelé par diagnostic.js.
 *
 * Deux couches, volontairement séparées :
 *   - `select()` ne raisonne que besoin / budget, et ignore le stock. Il peut
 *     donc désigner un produit non vendable — c'est voulu.
 *   - `resolve()` est la seule couche qui regarde la disponibilité.
 *
 * Les prix ci-dessous sont ceux de l'onglet « Catalogue v2 » : ils servent
 * uniquement à arbitrer (comparaison aux paliers de budget, ordre des
 * dégradations). `applyLive()` les rafraîchit avec les données réelles de la
 * boutique avant la résolution, et l'affichage se fait de toute façon sur le
 * produit live.
 *
 * Écarts v2 par rapport à v1 :
 *   - les cinq produits non vendables (CORNER, LAMA, WAVE, WAVE PREMIUM,
 *     SPHERA standard) sont sortis du catalogue, y compris des replis ;
 *   - le parcours bureau demande la taille du client (`taille`) : LIFT n'est
 *     proposé qu'à partir d'1,75 m, sa hauteur de travail minimale étant de
 *     72,5 cm ;
 *   - le parcours assise ne demande plus de budget ;
 *   - le parcours duo choisit la chaise d'abord, le bureau prenant
 *     l'enveloppe restante — c'est la chaise qui porte l'attente exprimée.
 */

(() => {
  /**
   * `slug` alimente les tags client et le paramètre d'URL de la page de reco.
   * Il est explicite plutôt que dérivé du handle : les handles sont longs et
   * changent au gré du SEO, alors qu'un tag doit rester stable.
   *
   * `sellable` part à `true` partout : l'onglet « Catalogue v2 » ne liste que
   * des produits vendables. Le drapeau reste utile, `applyLive()` le retourne
   * à partir du stock réel de la boutique.
   */
  const CATALOG = {
    LIFT: { price: 385, sellable: true, family: 'desk', slug: 'lift', handle: 'lift-bureau-assis-debout' },
    DYNAMIC: { price: 449, sellable: true, family: 'desk', slug: 'dynamic', handle: 'dynamic-bureau-assis-debout' },
    BAMBOU: { price: 516, sellable: true, family: 'desk', slug: 'bambou', handle: 'dynamic-bambou-bureau-assis-debout-bambou' },
    CONNECT: { price: 529, sellable: true, family: 'desk', slug: 'connect', handle: 'bureau-connecte-assis-debout-connect' },
    CURVE: { price: 549, sellable: true, family: 'desk', slug: 'curve', handle: 'dynamic-curve-bureau-assis-debout-incurve' },
    SPACE: { price: 555, sellable: true, family: 'desk', slug: 'space', handle: 'space-dynamic-bureau-assis-debout-avec-rangement' },
    SMOOTH: { price: 569, sellable: true, family: 'desk', slug: 'smooth', handle: 'smooth-dynamic-bureau-assis-debout-incurve' },
    EXECUTIVE: { price: 695, sellable: true, family: 'desk', slug: 'executive', handle: 'dynamic-executive-bureau-assis-debout-avec-plateau-en-verre' },

    ELENA: { price: 280, sellable: true, family: 'chair', slug: 'elena', handle: 'chaise-ergonomique-bureau-elena' },
    EPSILON: { price: 345, sellable: true, family: 'chair', slug: 'epsilon', handle: 'epsilon-chaise-ergonomique-de-bureau' },
    TERRANA: { price: 389, sellable: true, family: 'chair', slug: 'terrana', handle: 'terrana-chaise-ergonomique-de-bureau' },
    TERRANA_B: { price: 399, sellable: true, family: 'chair', slug: 'terrana-bouclette', handle: 'chaise-ergonomique-tissu-bouclette-terrana' },
    CLARA: { price: 415, sellable: true, family: 'chair', slug: 'clara', handle: 'chaise-ergonomique-bureau-clara' },
    TERRANA_M: { price: 419, sellable: true, family: 'chair', slug: 'terrana-maille', handle: 'chaise-ergonomique-de-bureau-terrana-en-maille' },
    TERRANA_P: { price: 515, sellable: true, family: 'chair', slug: 'terrana-premium', handle: 'chaise-ergonomique-bureau-terrana-premium' },
    TERRANA_PC: { price: 529, sellable: true, family: 'chair', slug: 'terrana-premium-cuir', handle: 'chaise-ergonomique-bureau-terrana-premium-noir-cuir-reconstitue' },
    AURA: { price: 535, sellable: true, family: 'chair', slug: 'aura', handle: 'chaise-ergonomique-gamer-aura-premium-noir-mousse-maille' },
    VERTEX: { price: 545, sellable: true, family: 'chair', slug: 'vertex', handle: 'chaise-ergonomique-bureau-vertex' },
    VERTEX_C: { price: 545, sellable: true, family: 'chair', slug: 'vertex-cuir', handle: 'chaise-ergonomique-de-bureau-vertex-en-cuir-reconstitue' },
    NOVA: { price: 629, sellable: true, family: 'chair', slug: 'nova', handle: 'siege-ergonomique-haut-de-gamme-nova' },
    FLOW: { price: 649, sellable: true, family: 'chair', slug: 'flow', handle: 'chaise-active-dynamique-flow' },

    SPHERA_B: { price: 99, sellable: true, family: 'seat', slug: 'sphera-bouclette', handle: 'ballon-ergonomique-sphera-tissu-bouclette' },
    WOOBLE: { price: 144, sellable: true, family: 'seat', slug: 'wooble', handle: 'wooble-tabouret-assis-debout' },
    MOOVE: { price: 164, sellable: true, family: 'seat', slug: 'moove', handle: 'moove-chaise-de-bureau-ergonomique' },
    MOOVE_B: { price: 174, sellable: true, family: 'seat', slug: 'moove-bouclette', handle: 'chaise-assis-genoux-moove-tissu-bouclette' },
    ZEN: { price: 179, sellable: true, family: 'seat', slug: 'zen', handle: 'chaise-ergonomique-assis-en-tailleur-zen' },
    ZEN_B: { price: 189, sellable: true, family: 'seat', slug: 'zen-bouclette', handle: 'chaise-assis-en-tailleur-zen-tissu-bouclette' }
  };

  /** Replis de disponibilité — produit proche, pas produit moins cher. */
  const FALLBACK = {
    CONNECT: ['EXECUTIVE', 'SMOOTH', 'DYNAMIC'],
    SMOOTH: ['CURVE', 'SPACE', 'DYNAMIC'],
    CURVE: ['SMOOTH', 'SPACE', 'DYNAMIC'],
    // CURVE en tête : c'est le plateau voisin en gamme et en prix. Descendre
    // droit sur DYNAMIC ferait perdre 100 € de positionnement d'un coup.
    SPACE: ['CURVE', 'DYNAMIC', 'LIFT'],
    BAMBOU: ['DYNAMIC', 'SPACE', 'LIFT'],
    EXECUTIVE: ['CONNECT', 'SMOOTH', 'DYNAMIC'],
    DYNAMIC: ['LIFT', 'SPACE'],
    LIFT: ['DYNAMIC'],
    ELENA: ['EPSILON', 'TERRANA'],
    EPSILON: ['ELENA', 'TERRANA'],
    TERRANA: ['TERRANA_M', 'TERRANA_B', 'EPSILON'],
    TERRANA_B: ['TERRANA', 'TERRANA_M'],
    TERRANA_M: ['TERRANA', 'CLARA', 'TERRANA_B'],
    TERRANA_P: ['AURA', 'VERTEX', 'TERRANA_M'],
    TERRANA_PC: ['VERTEX_C', 'VERTEX', 'TERRANA_P'],
    VERTEX: ['AURA', 'TERRANA_P', 'NOVA', 'TERRANA_M'],
    VERTEX_C: ['TERRANA_PC', 'VERTEX', 'AURA'],
    NOVA: ['FLOW', 'VERTEX', 'AURA'],
    FLOW: ['NOVA', 'VERTEX', 'TERRANA_P'],
    CLARA: ['TERRANA', 'TERRANA_M'],
    AURA: ['VERTEX', 'TERRANA_P', 'TERRANA_M'],
    SPHERA_B: ['WOOBLE', 'MOOVE'],
    WOOBLE: ['SPHERA_B', 'MOOVE'],
    MOOVE: ['MOOVE_B', 'ZEN'],
    MOOVE_B: ['MOOVE', 'ZEN_B'],
    ZEN: ['ZEN_B', 'MOOVE'],
    ZEN_B: ['ZEN', 'MOOVE_B']
  };

  const FAMILY_SAFETY = {
    desk: ['DYNAMIC', 'LIFT'],
    chair: ['TERRANA', 'TERRANA_B'],
    seat: ['ZEN', 'ZEN_B']
  };

  /** Chaînes de dégradation, triées par prix décroissant. */
  const CHAINS = {
    desk: ['EXECUTIVE', 'SMOOTH', 'SPACE', 'CURVE', 'CONNECT', 'BAMBOU', 'DYNAMIC', 'LIFT'],
    chair: [
      'FLOW', 'NOVA', 'VERTEX_C', 'VERTEX', 'AURA', 'TERRANA_PC', 'TERRANA_P',
      'TERRANA_M', 'CLARA', 'TERRANA_B', 'TERRANA', 'EPSILON', 'ELENA'
    ],
    seat: ['ZEN_B', 'ZEN', 'MOOVE_B', 'MOOVE', 'WOOBLE', 'SPHERA_B']
  };

  /**
   * Ladders de bureau du parcours duo, par priorité. Le premier élément qui
   * tient dans l'enveloppe restante est retenu ; les modèles sautés (BAMBOU,
   * CONNECT hors MAX) ne répondent pas à la priorité exprimée.
   */
  const DUO_DESKS = {
    qp: ['SMOOTH', 'SPACE', 'DYNAMIC', 'LIFT'],
    rangement: ['SPACE', 'CURVE', 'DYNAMIC', 'LIFT'],
    design: ['CURVE', 'DYNAMIC', 'LIFT'],
    designMax: ['CONNECT', 'CURVE', 'DYNAMIC', 'LIFT']
  };

  const price = (key) => CATALOG[key].price;
  const family = (key) => CATALOG[key].family;
  const sellable = (key) => CATALOG[key].sellable;

  /**
   * @param {string} budget - Valeur de la réponse budget, ou rien.
   * @returns {number} Plafond numérique. `MAX` n'a pas de plafond, et le
   *   parcours assise ne pose plus la question du tout.
   */
  const ceilOf = (budget) => {
    const value = Number(budget);
    return Number.isFinite(value) && value > 0 ? value : Infinity;
  };

  /** Élément suivant (moins cher) de la chaîne. */
  const nextIn = (chain, key) => {
    const list = CHAINS[chain];
    const i = list.indexOf(key);
    return i === -1 || i === list.length - 1 ? key : list[i + 1];
  };

  /** Produit le plus cher de la chaîne dont le prix tient dans le plafond. */
  const clamp = (chain, key, ceil) => {
    if (price(key) <= ceil) return key;
    const found = CHAINS[chain].find((k) => price(k) <= ceil);
    return found || CHAINS[chain][CHAINS[chain].length - 1];
  };

  /**
   * Parcours A — bureau (onglet « A — Bureau v2 »).
   * @param {Object<string, string>} a - Réponses.
   * @returns {{main: string[], cross: string[]}}
   */
  function selectDesk(a) {
    const ceil = ceilOf(a.budget);

    let d;
    if (a.prio === 'ecolo') {
      d = ceil <= 450 ? 'DYNAMIC' : 'BAMBOU';
    } else if (a.prio === 'rangement') {
      d = ceil <= 450 ? 'DYNAMIC' : ceil <= 550 ? 'CURVE' : 'SPACE';
    } else if (a.prio === 'design') {
      d = ceil <= 450 ? 'DYNAMIC' : ceil <= 550 ? 'CURVE' : ceil <= 700 ? 'SMOOTH' : 'EXECUTIVE';
    } else if (ceil <= 550) {
      // Rapport qualité-prix. LIFT n'apparaît qu'ici, et seulement à partir
      // d'1,75 m (garde-fou plus bas) : en dessous, sa hauteur de travail
      // minimale de 72,5 cm est déjà trop haute.
      d = ceil <= 450 ? 'LIFT' : 'DYNAMIC';
    } else if (a.space === 'petit') {
      // 120 cm de large : au-delà de DYNAMIC, les plateaux n'existent pas dans
      // ce format — monter en budget n'apporterait rien.
      d = 'DYNAMIC';
    } else {
      d = ceil <= 700 ? 'SMOOTH' : 'CONNECT';
    }

    d = clamp('desk', d, ceil);

    // Règle hauteur, posée après le clamp : sinon un prix live plus élevé
    // pourrait faire retomber la reco sur LIFT à l'insu de la règle.
    if (d === 'LIFT' && a.taille !== '175-plus') d = 'DYNAMIC';

    return { main: [d], cross: [ceil <= 450 ? 'ELENA' : 'TERRANA'] };
  }

  /**
   * Parcours B — chaise (onglet « B — Chaise v2 »).
   * @param {Object<string, string>} a - Réponses.
   * @returns {{main: string[], cross: string[]}}
   */
  function selectChair(a) {
    const ceil = ceilOf(a.budget);
    const intense = a.time === 'high';

    let c;
    if (ceil <= 350) {
      // « Jusqu'à 350 € » saute l'écran matière : sous ce plafond EPSILON est
      // la seule assise de la gamme, la question n'aurait pas de réponse.
      c = 'EPSILON';
    } else if (a.matiere === 'maille') {
      c = 'TERRANA_M';
    } else if (a.matiere === 'bouclette') {
      c = 'TERRANA_B';
    } else if (a.matiere === 'cuir') {
      c = ceil <= 450 ? 'TERRANA' : ceil <= 550 ? 'TERRANA_PC' : 'VERTEX_C';
    } else if (a.prio === 'respirabilite') {
      c = 'TERRANA_M';
    } else if (a.prio === 'cosy') {
      c = 'TERRANA_B';
    } else if (a.prio === 'maintien') {
      c = ceil <= 450 ? 'TERRANA' : ceil <= 550 ? (intense ? 'VERTEX' : 'TERRANA_P') : 'NOVA';
    } else if (!intense || ceil <= 450) {
      // Rapport qualité-prix : sans usage intensif, monter en gamme n'apporte
      // rien à ce client — TERRANA suffit, quel que soit le budget annoncé.
      c = 'TERRANA';
    } else {
      c = ceil <= 550 ? 'VERTEX' : 'FLOW';
    }

    return { main: [clamp('chair', c, ceil)], cross: [ceil <= 350 ? 'LIFT' : 'DYNAMIC'] };
  }

  /**
   * Parcours C — duo bureau + chaise (onglet « C — Duo v2 »).
   *
   * La chaise est choisie d'abord : c'est elle qui porte le temps passé assis
   * et l'attente exprimée. Le bureau prend l'enveloppe restante en descendant
   * le ladder de sa priorité ; si même le moins cher ne tient pas, la chaise
   * redescend d'un cran et le bureau est rejoué.
   *
   * @param {Object<string, string>} a - Réponses.
   * @returns {{main: string[], cross: string[]}}
   */
  function selectDuo(a) {
    const ceil = ceilOf(a.budget);
    const hard = a.time === 'high' || a.prio === 'maintien';
    const cosy = a.style === 'bouclette';

    let c;
    if (cosy) {
      // La bouclette demandée l'emporte sur le maintien : TERRANA bouclette,
      // sauf sous 750 € où le couple ne rentre pas.
      c = ceil <= 750 ? 'EPSILON' : 'TERRANA_B';
    } else if (hard) {
      c = ceil <= 750 ? 'EPSILON' : ceil <= 950 ? 'TERRANA_M' : 'VERTEX';
    } else {
      c = ceil <= 750 ? 'ELENA' : ceil <= 950 ? 'TERRANA' : 'TERRANA_B';
    }

    const desks =
      a.prio === 'rangement' ? DUO_DESKS.rangement
      : a.prio === 'design' ? (ceil === Infinity ? DUO_DESKS.designMax : DUO_DESKS.design)
      : DUO_DESKS.qp.slice(
        // Sans priorité portée par un modèle précis, le haut du ladder suit le
        // palier de budget : au-delà, un plateau plus cher n'apporte rien.
        DUO_DESKS.qp.indexOf(ceil <= 750 ? 'LIFT' : ceil <= 950 ? 'DYNAMIC' : ceil <= 1200 ? 'SPACE' : 'SMOOTH')
      );

    const fit = () => desks.find((k) => price(k) + price(c) <= ceil) || desks[desks.length - 1];

    let d = fit();
    for (let i = 0; i < CHAINS.chair.length && price(d) + price(c) > ceil; i += 1) {
      const cheaper = nextIn('chair', c);
      if (cheaper === c) break;
      c = cheaper;
      d = fit();
    }

    return { main: [d, c], cross: [] };
  }

  /**
   * Parcours D — assise alternative (onglet « D — Assise v2 »).
   * Plus de question de budget : les six assises tiennent sous 200 €.
   * @param {Object<string, string>} a - Réponses.
   * @returns {{main: string[], cross: string[]}}
   */
  function selectSeat(a) {
    const cosy = a.style === 'bouclette';

    const s =
      a.objectif === 'tailleur' ? (cosy ? 'ZEN_B' : 'ZEN')
      : a.objectif === 'dos' ? (cosy ? 'MOOVE_B' : 'MOOVE')
      : a.objectif === 'gainage' ? 'SPHERA_B'
      : 'WOOBLE';

    // Deuxième assise à essayer, puis le bureau qui va avec le lieu d'usage.
    const alt =
      a.objectif === 'dos' ? 'ZEN'
      : a.objectif === 'gainage' ? 'WOOBLE'
      : 'MOOVE';

    return { main: [s], cross: [alt, a.lieu === 'debout' ? 'DYNAMIC' : 'LIFT'] };
  }

  /**
   * Choix commercial pur — ignore volontairement le stock.
   * @param {Object<string, string>} a - Réponses, par clé de question.
   * @returns {{main: string[], cross: string[]}}
   */
  function select(a) {
    if (a.need === 'bureau') return selectDesk(a);
    if (a.need === 'chaise') return selectChair(a);
    if (a.need === 'duo') return selectDuo(a);
    return selectSeat(a);
  }

  /**
   * Seule couche qui regarde la disponibilité.
   * @param {string} key - Produit retenu par `select()`.
   * @param {number} ceil - Plafond de budget.
   * @param {Object<string, string>} [answers] - Réponses, pour les règles qui
   *   doivent survivre au repli (la hauteur de travail de LIFT).
   * @returns {string} Le produit réellement affichable.
   */
  function resolve(key, ceil, answers) {
    const safety = FAMILY_SAFETY[family(key)];
    const short = Boolean(answers && answers.taille) && answers.taille !== '175-plus';
    const allowed = (k) => !(short && k === 'LIFT');
    const candidates = [key, ...(FALLBACK[key] || []), ...safety].filter(allowed);

    // 1 · le produit choisi tient : on le garde, même s'il est cher.
    if (allowed(key) && sellable(key) && price(key) <= ceil) return key;

    // 2 · sinon, premier repli vendable dans le budget.
    const inBudget = candidates.find((c) => sellable(c) && price(c) <= ceil);
    if (inBudget) return inBudget;

    // 3 · budget relâché plutôt que rien : le moins cher des vendables.
    const anySellable = candidates.filter(sellable);
    if (anySellable.length) {
      return anySellable.reduce((best, c) => (price(c) < price(best) ? c : best));
    }

    return candidates[candidates.length - 1] || safety[0];
  }

  /** Handles à rafraîchir pour une réponse donnée : le choix et ses replis. */
  function candidateKeys(answers) {
    const chosen = select(answers);
    const keys = new Set();

    chosen.main.forEach((key) => {
      keys.add(key);
      (FALLBACK[key] || []).forEach((k) => keys.add(k));
      FAMILY_SAFETY[family(key)].forEach((k) => keys.add(k));
    });

    return [...keys];
  }

  /**
   * Remplace prix et disponibilité par les données réelles de la boutique.
   * @param {string} key - Clé catalogue.
   * @param {object} product - Réponse de /products/{handle}.js
   */
  function applyLive(key, product) {
    if (!CATALOG[key] || !product || !Array.isArray(product.variants)) return;

    const available = product.variants.filter((v) => v.available);
    CATALOG[key].sellable = available.length > 0;

    // Prix mini des variantes *disponibles* : `price_min` pointe parfois vers
    // une variante en rupture (LIFT annoncé 330 €, 385 € en pratique).
    if (available.length) {
      CATALOG[key].price = Math.min(...available.map((v) => v.price)) / 100;
    }
  }

  /**
   * Recommandation finale : produits résolus, plus le tag / paramètre d'URL
   * qui les nomme.
   *
   * `cross` ne pilote rien : le complément réellement affiché sous une fiche
   * vient du métafield `custom.diagnostic_cross_sell` du produit recommandé
   * (snippets/diagnostic-reco-cross.liquid), donc une reco duo affiche celui
   * du bureau et celui de la chaise. La valeur rendue ici ne sert qu'à
   * transcrire la colonne « Cross-sell » du tableur, à titre de trace.
   *
   * @param {Object<string, string>} answers - Réponses, par clé de question.
   * @returns {{keys: string[], cross: string[], slug: string, products: object[]}}
   */
  function recommend(answers) {
    const ceil = ceilOf(answers.budget);
    const chosen = select(answers);
    let keys;

    if (answers.need === 'duo') {
      const [d, c] = chosen.main;
      // Budget restant pièce par pièce, sinon un repli plus cher fait sauter
      // l'enveloppe du couple.
      keys = [resolve(d, ceil - price(c), answers), resolve(c, ceil - price(d), answers)];
    } else {
      keys = chosen.main.map((key) => resolve(key, ceil, answers));
    }

    const slugs = keys.map((key) => CATALOG[key].slug);
    const prefix = answers.need === 'assise' ? 'alternative' : answers.need;

    return {
      keys,
      cross: chosen.cross,
      slug: `diagnostic-${prefix}-${slugs.join('-')}`,
      products: keys.map((key) => ({ key, ...CATALOG[key] }))
    };
  }

  window.DiagnosticEngine = {
    CATALOG,
    FALLBACK,
    FAMILY_SAFETY,
    CHAINS,
    DUO_DESKS,
    select,
    resolve,
    recommend,
    candidateKeys,
    applyLive
  };
})();
